import type { Role, CurrentUserInfo } from '../services/usersService'

export type CodesheetAction =
  | 'view_codesheet'
  | 'edit_codesheet'
  | 'delete_selection'
  | 'save_changes'
  | 'submit_changes'
  | 'cancel_project'
  | 'revise_change'
  | 'duplicate_codesheet'
  | 'manage_comments'
  | 'view_version_history'
  | 'preview_version'
  | 'rollback_version'
  | 'view_changes_requested'
  | 'review_changeset'
  | 'compare_versions'
  | 'merge_changeset'
  | 'assign_collaborators'
  | 'remove_collaborators'

export type ActionAccessBlockReason = 'missing_permission' | 'wrong_business_unit' | null

export interface ActionAccessResult {
  allowed: boolean
  blockedBy: ActionAccessBlockReason
  message: string | null
}

export interface CodesheetAccessContext {
  user: Pick<CurrentUserInfo, 'businessUnit'> | null
  roles?: Array<Pick<Role, 'name'> | null | undefined>
  permissions?: Iterable<string> | null
  codesheetBusinessUnit?: string | null
}

type ActionRule = {
  permissions: string[]
  enforceBusinessUnit: boolean
}

const WRONG_BUSINESS_UNIT_MESSAGE = 'This Code Sheet belongs to {codesheetBU}. You can view it, but you can only modify Code Sheets in your own Business Unit.'
const MISSING_PERMISSION_MESSAGE = 'You do not have permission to perform this action.'
const MODIFY_CODESHEET_PERMISSION_MESSAGE = 'Your current role does not include the permission required to modify this Code Sheet.'
const GLOBAL_ADMIN_ROLE_NAMES = new Set(['admin'])

const ACTION_RULES: Record<CodesheetAction, ActionRule> = {
  view_codesheet: { permissions: ['View_Codesheets'], enforceBusinessUnit: false },
  edit_codesheet: { permissions: ['Update_Codesheets'], enforceBusinessUnit: true },
  delete_selection: { permissions: ['Delete_Codesheets'], enforceBusinessUnit: true },
  save_changes: { permissions: ['Publish_Codesheets'], enforceBusinessUnit: true },
  submit_changes: { permissions: ['Submit_Changeset'], enforceBusinessUnit: true },
  cancel_project: { permissions: ['Delete_Codesheets'], enforceBusinessUnit: true },
  revise_change: { permissions: ['Update_Codesheets'], enforceBusinessUnit: true },
  duplicate_codesheet: { permissions: ['Create_Codesheets', 'Update_Codesheets'], enforceBusinessUnit: true },
  manage_comments: { permissions: ['Comment_Codesheets'], enforceBusinessUnit: true },
  view_version_history: { permissions: ['View_Version_History'], enforceBusinessUnit: false },
  preview_version: { permissions: ['View_Version_History'], enforceBusinessUnit: false },
  rollback_version: { permissions: ['Rollback_Version'], enforceBusinessUnit: true },
  view_changes_requested: { permissions: ['View_Codesheets'], enforceBusinessUnit: false },
  review_changeset: { permissions: ['Review_Changeset'], enforceBusinessUnit: true },
  compare_versions: { permissions: ['Compare_Versions'], enforceBusinessUnit: false },
  merge_changeset: { permissions: ['Merge_Changeset'], enforceBusinessUnit: true },
  assign_collaborators: { permissions: ['Assign_Collaborators'], enforceBusinessUnit: true },
  remove_collaborators: { permissions: ['Remove_Collaborators'], enforceBusinessUnit: true },
}

function normalizeText(value: string | null | undefined): string {
  return String(value || '').trim()
}

function normalizeLookupKey(value: string | null | undefined): string {
  return normalizeText(value).toLowerCase()
}

export function isGlobalAdminRole(roleName: string | null | undefined): boolean {
  return GLOBAL_ADMIN_ROLE_NAMES.has(normalizeLookupKey(roleName))
}

export function isGlobalAdmin(roles?: Array<Pick<Role, 'name'> | null | undefined>): boolean {
  return Array.isArray(roles) && roles.some((role) => isGlobalAdminRole(role?.name))
}

function buildMissingPermissionMessage(rule: ActionRule): string {
  return rule.enforceBusinessUnit
    ? MODIFY_CODESHEET_PERMISSION_MESSAGE
    : MISSING_PERMISSION_MESSAGE
}

function buildWrongBusinessUnitMessage(codesheetBusinessUnit?: string | null): string {
  const normalizedBusinessUnit = normalizeText(codesheetBusinessUnit) || 'another Business Unit'
  return WRONG_BUSINESS_UNIT_MESSAGE.replace('{codesheetBU}', normalizedBusinessUnit)
}

function hasRequiredPermissions(
  permissions: Iterable<string> | null | undefined,
  requiredPermissions: string[]
): boolean {
  const grantedPermissions = new Set<string>()

  if (Array.isArray(permissions)) {
    permissions.forEach((permission) => {
      grantedPermissions.add(permission)
    })
  } else if (permissions && typeof (permissions as Set<string>).forEach === 'function') {
    ;(permissions as Set<string>).forEach((permission) => {
      grantedPermissions.add(permission)
    })
  }

  return requiredPermissions.every((permission) => grantedPermissions.has(permission))
}

function isSameBusinessUnit(
  userBusinessUnit: string | null | undefined,
  codesheetBusinessUnit: string | null | undefined
): boolean {
  const normalizedUserBusinessUnit = normalizeLookupKey(userBusinessUnit)
  const normalizedCodesheetBusinessUnit = normalizeLookupKey(codesheetBusinessUnit)

  if (!normalizedCodesheetBusinessUnit) {
    return true
  }

  return normalizedUserBusinessUnit !== '' && normalizedUserBusinessUnit === normalizedCodesheetBusinessUnit
}

export function getCodesheetActionAccess(
  context: CodesheetAccessContext,
  action: CodesheetAction
): ActionAccessResult {
  const rule = ACTION_RULES[action]
  if (!rule) {
    return {
      allowed: false,
      blockedBy: 'missing_permission',
      message: MISSING_PERMISSION_MESSAGE,
    }
  }

  if (!hasRequiredPermissions(context.permissions, rule.permissions)) {
    return {
      allowed: false,
      blockedBy: 'missing_permission',
      message: buildMissingPermissionMessage(rule),
    }
  }

  if (
    rule.enforceBusinessUnit &&
    !isGlobalAdmin(context.roles) &&
    !isSameBusinessUnit(context.user?.businessUnit, context.codesheetBusinessUnit)
  ) {
    return {
      allowed: false,
      blockedBy: 'wrong_business_unit',
      message: buildWrongBusinessUnitMessage(context.codesheetBusinessUnit),
    }
  }

  return {
    allowed: true,
    blockedBy: null,
    message: null,
  }
}
